import type { App } from '../app/App';
import { MAX_CART, cartSkus, type CheckoutResponse, type PurchaseResponse, type ShopResponse } from '@shared/shop';
import { language, t } from '../i18n';

/** The last shop answer for this player, reused for two minutes (the Gear page redraws often). */
let shopCache: { token: string | null; at: number; p: Promise<ShopResponse> } | null = null;
const SHOP_CACHE_MS = 120_000;

/** GET /api/shop (works signed out too: owned is then empty). */
export function fetchShop(app: App): Promise<ShopResponse> {
  const token = app.store.state.online?.token ?? null;
  app.api.token = token;
  if (shopCache && shopCache.token === token && Date.now() - shopCache.at < SHOP_CACHE_MS) return shopCache.p;
  const p = app.api.raw<ShopResponse>('GET', '/api/shop');
  shopCache = { token, at: Date.now(), p };
  p.catch(() => (shopCache = null));
  return p;
}

/** Adds items the server says this player owns (purchases) to the notebook. */
export function mergeOwned(app: App, owned: readonly string[]): string[] {
  const added = owned.filter((id) => !app.store.state.cosmetics.owned.includes(id));
  if (added.length) app.store.update(['inventory'], (s) => s.cosmetics.owned.push(...added));
  return added;
}

/** Brings purchases made on other devices into this notebook. Quiet when offline or signed out. */
export async function syncEntitlements(app: App): Promise<void> {
  if (!app.store.state.online) return;
  try {
    mergeOwned(app, (await fetchShop(app)).owned);
  } catch {
    /* offline or the Go dev server: try again next start */
  }
}

const CART_KEY = 'paper-legends/cart';

/** Skus waiting in the shopping cart (kept in this browser only). */
export function loadCart(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CART_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, MAX_CART) : [];
  } catch {
    return [];
  }
}

export function saveCart(skus: readonly string[]): void {
  try {
    if (skus.length) localStorage.setItem(CART_KEY, JSON.stringify(skus.slice(0, MAX_CART)));
    else localStorage.removeItem(CART_KEY);
  } catch {
    /* private window: the cart just lives until the page closes */
  }
}

/** Opens Stripe's payment page for everything in the cart at once. */
export async function startCartCheckout(app: App, skus: readonly string[]): Promise<void> {
  app.store.flush();
  await app.cloud.syncNow();
  const res = await app.api.raw<CheckoutResponse>('POST', '/api/shop/cart', { skus, lang: language() });
  location.href = res.url;
}

/** Opens Stripe's payment page for a product. */
export async function startCheckout(app: App, sku: string): Promise<void> {
  app.store.flush();
  await app.cloud.syncNow();
  const res = await app.api.raw<CheckoutResponse>('POST', '/api/shop/checkout', { sku, lang: language() });
  location.href = res.url;
}

/**
 * Back from Stripe (?purchase=<session id> or ?purchase=cancelled): waits for
 * the payment to be confirmed, adds the items and thanks the player.
 * PromptPay can take a little while, so it keeps checking for a minute.
 */
export async function handlePurchaseReturn(app: App): Promise<void> {
  const url = new URL(location.href);
  const id = url.searchParams.get('purchase');
  if (!id) return;
  url.searchParams.delete('purchase');
  history.replaceState(null, '', url.pathname + url.search + url.hash);
  if (id === 'cancelled') {
    app.toast(t('Payment cancelled. Nothing was charged.'), 'info');
    return;
  }
  if (!app.store.state.online) return;
  app.api.token = app.store.state.online.token;
  app.toast(t('Checking your payment…'), 'info');
  for (let i = 0; i < 30; i++) {
    try {
      const p = await app.api.raw<PurchaseResponse>('GET', `/api/shop/purchases/${encodeURIComponent(id)}`);
      if (p.status === 'paid') {
        shopCache = null; // the shelf shows the new items as owned
        mergeOwned(app, p.owned);
        // What was just bought leaves the cart.
        const bought = new Set(cartSkus(p.sku));
        saveCart(loadCart().filter((s) => !bought.has(s)));
        app.sfx.play('pop');
        app.toast(t('Thank you! Your items are in the Stationery Shop.'), 'success');
        return;
      }
      if (p.status === 'failed') {
        app.toast(t('The payment did not go through. Nothing was charged.'), 'error');
        return;
      }
    } catch {
      /* try again */
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  app.toast(t('Your payment is still being confirmed. Your items will appear as soon as it is done.'), 'info');
}
